import type {
  KYCQuestionDefinition,
  KYCQuestionnaireElement,
} from '@anchorage/graphql/generated';
import type { ClientTaxDocumentation } from '@taxbit/utilities';
import {
  auStateCodes,
  caProvinceCodes,
  countryCodes,
  usStateCodes,
} from '@taxbit/utilities';
import type { FormAnswer } from 'hooks/useQuestionnaire/types';
import {
  getElementQuestions,
  getOptionTagById,
} from 'hooks/useQuestionnaire/utils';

import type { TaxPrefillQuestionIDs } from './taxPrefillQuestionIDs';
import {
  ENTITY_TAX_PREFILL_QUESTION_IDS,
  HNWI_TAX_PREFILL_QUESTION_IDS,
} from './taxPrefillQuestionIDs';

type FormValues = Record<string, FormAnswer | undefined>;
type QuestionsByID = Map<string, KYCQuestionDefinition>;

export type TaxPrefillMapping = {
  questionIDs: TaxPrefillQuestionIDs;
  isIndividual: boolean;
};

const TAX_PREFILL_MAPPINGS_BY_TAX_QUESTION_ID = new Map<
  string,
  TaxPrefillMapping
>([
  [
    'taxCertification',
    { questionIDs: ENTITY_TAX_PREFILL_QUESTION_IDS, isIndividual: false },
  ],
  [
    'taxCertificationHnwi',
    { questionIDs: HNWI_TAX_PREFILL_QUESTION_IDS, isIndividual: true },
  ],
]);

/**
 * Returns the prefill mapping for a tax certification question, or
 * `undefined` for a question id with no mapping.
 */
export const getTaxPrefillMapping = (
  taxQuestionID: string,
): TaxPrefillMapping | undefined =>
  TAX_PREFILL_MAPPINGS_BY_TAX_QUESTION_ID.get(taxQuestionID);

const US_COUNTRY_CODE = 'US';
const YES_OPTION_TAG = 'TRUE';

const TAXBIT_COUNTRY_CODES = new Set<string>(countryCodes);

// The SDK keeps a stateOrProvince for these countries only when it is a code
// from its own list; for any other country it keeps the free text.
const TAXBIT_STATE_CODES_BY_COUNTRY: Record<string, string[]> = {
  [US_COUNTRY_CODE]: usStateCodes,
  CA: caProvinceCodes,
  AU: auStateCodes,
};

const getQuestionsByID = (
  questionnaireBody: KYCQuestionnaireElement,
): QuestionsByID =>
  new Map(
    getElementQuestions(questionnaireBody).map((question) => [
      question.id,
      question,
    ]),
  );

const getText = (formValues: FormValues, questionID: string) =>
  formValues[questionID]?.freeformText?.trim() || undefined;

const getSelectedOptionTag = (
  formValues: FormValues,
  question: KYCQuestionDefinition | undefined,
) => {
  const selected = question && formValues[question.id]?.options;
  const optionID = Array.isArray(selected) ? selected[0] : selected;
  if (!question || !optionID) {
    return undefined;
  }
  return getOptionTagById(question.options ?? [], optionID) || undefined;
};

const getCountryCode = (
  formValues: FormValues,
  question: KYCQuestionDefinition | undefined,
) => {
  const countryCode = getSelectedOptionTag(formValues, question);
  return countryCode && TAXBIT_COUNTRY_CODES.has(countryCode)
    ? countryCode
    : undefined;
};

const getStateOrProvince = (
  formValues: FormValues,
  questionsByID: QuestionsByID,
  questionIDs: TaxPrefillQuestionIDs,
  countryCode: string | undefined,
) => {
  if (!countryCode) {
    return undefined;
  }
  const stateOrProvince =
    countryCode === US_COUNTRY_CODE
      ? getSelectedOptionTag(formValues, questionsByID.get(questionIDs.stateUS))
      : getText(formValues, questionIDs.state);
  const allowedCodes = TAXBIT_STATE_CODES_BY_COUNTRY[countryCode];
  if (!stateOrProvince || !allowedCodes) {
    return stateOrProvince;
  }
  const stateCode = stateOrProvince.toUpperCase();
  return allowedCodes.includes(stateCode) ? stateCode : undefined;
};

const getTin = (
  formValues: FormValues,
  questionsByID: QuestionsByID,
  questionIDs: TaxPrefillQuestionIDs,
) => {
  const hasUsTaxpayer =
    getSelectedOptionTag(
      formValues,
      questionsByID.get(questionIDs.hasUsTaxpayer),
    ) === YES_OPTION_TAG;
  const tin = getText(formValues, questionIDs.tin);
  return hasUsTaxpayer && tin && /^\d{9}$/.test(tin) ? tin : undefined;
};

/**
 * Returns the question ids of the mapping that the questionnaire does not
 * contain. A non-empty result means a mapped question was renamed or removed,
 * so its prefill field will always be empty.
 */
export const getMissingTaxPrefillQuestionIDs = (
  questionnaireBody: KYCQuestionnaireElement,
  { questionIDs }: TaxPrefillMapping,
): string[] => {
  const questionsByID = getQuestionsByID(questionnaireBody);
  return Object.values(questionIDs).filter(
    (questionID) => !questionsByID.has(questionID),
  );
};

// The SDK merges `data` over the saved submission key by key, and a key set to
// `undefined` erases the saved value, so unanswered fields must be left out.
const omitUndefined = <T extends object>(value: T): T =>
  Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as T;

/**
 * Builds the TaxBit SDK `data` prefill from the onboarding form values, using
 * the question ids of the given mapping (entity or HNWI). Only answered fields
 * are present.
 */
export const getTaxbitPrefillFromAnswers = (
  formValues: FormValues,
  questionnaireBody: KYCQuestionnaireElement,
  { questionIDs, isIndividual }: TaxPrefillMapping,
): ClientTaxDocumentation => {
  const questionsByID = getQuestionsByID(questionnaireBody);

  const addressCountry = getCountryCode(
    formValues,
    questionsByID.get(questionIDs.country),
  );

  const address = omitUndefined({
    country: addressCountry,
    firstLine: getText(formValues, questionIDs.streetAddress),
    secondLine: getText(formValues, questionIDs.streetAddressSecond),
    city: getText(formValues, questionIDs.city),
    stateOrProvince: getStateOrProvince(
      formValues,
      questionsByID,
      questionIDs,
      addressCountry,
    ),
    postalCode: getText(formValues, questionIDs.zipCode),
  });

  return {
    accountHolder: omitUndefined({
      isIndividual,
      name: getText(formValues, questionIDs.name),
      countryOfCitizenship: getCountryCode(
        formValues,
        questionsByID.get(questionIDs.incorporationCountry),
      ),
      tin: getTin(formValues, questionsByID, questionIDs),
      address: Object.keys(address).length > 0 ? address : undefined,
    }),
  };
};
